import { Injectable, OnDestroy } from '@angular/core';
import { filter, Observable, of, Subscription } from 'rxjs';
import { GoogleAnalyticsReportResponseModel } from '../../../api/externalData/models/google-analytics-report.response';
import { GoogleAnalyticsReportTypesEnum } from '../../../api/externalData/enums/google-analytics-report-types.enum';
import { DataPointModel } from '../../Charts/models/data-point.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { ReportSeriesModel } from '../models/report-series.model';
import moment from 'moment-timezone';
import { ExternalChartMetricsEnum } from '../enums/external-chart-metrics.enum';
import { ReportsDataMappingKeysEnum } from '../models/reports-block-mapping-configuration.model';
import { AgprMath } from '../../../Common/Misc/helpers/math/math-helper';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { getTimeScale } from '../../../Common/Forms/helpers/time.helper';


const enum ReferralSourceType {
    NEWVISITOR = 'NEWVISITOR',
    RETURNVISITOR = 'RETURNVISITOR'
}

@Injectable()
export class ReportsExternalDataMapperService implements OnDestroy {

    userPreferenceTimezone;

    susbcriptions: Subscription[] = []

    readonly oneDay = 86400000;

    /**
     * Class Constructor
     */
    constructor(
        public currentUserStateService: CurrentUserStateService,
        private localizationService: LocalizationService
    ) {
        this.susbcriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(x => this.userPreferenceTimezone = x.timeZone)
        );
    }

    ngOnDestroy(): void {
        this.susbcriptions.forEach(x => x.unsubscribe());
    }

    /**
     * Responsible for determining and calling the appropriate map function
     */
    public mapGoogleAnalytics(series: ReportSeriesModel, response: GoogleAnalyticsReportResponseModel): Observable<DataPointModel[]> {
        let dataPoints: Observable<DataPointModel[]>;
        const chartType: ReportBlockTypeEnum = series.chartType;

        switch (chartType) {
            case ReportBlockTypeEnum.REFERRAL_TREND:
                dataPoints = this.mapReferralTrend(response, series);
                break;
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                dataPoints = this.mapCoverageImpactTrend(response, series);
                break;
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                dataPoints = this.mapSocialReferrers(response, series);
                break;
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
                dataPoints = this.mapReferralSources(response);
                break;
        }

        return dataPoints;
    }

    private mapSocialReferrers(
        response: GoogleAnalyticsReportResponseModel,
        series: ReportSeriesModel
    ): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];

        let responseArray = Object.keys(response.sumOfCountsByBucketType).map(key => {
            let label = key.split('_')[0].toLocaleLowerCase();
            label = label.charAt(0).toUpperCase() + label.slice(1);

            return {
                label: label,
                count: response.sumOfCountsByBucketType[key]
            };
        });
        responseArray = responseArray.sort((a, b) => b.count - a.count);
        const totalResults = responseArray.reduce((sum, val) => sum + val.count, 0);
        for (const point of responseArray) {
            data.push({
                label: point.label,
                mappingKeys: {
                    [ReportsDataMappingKeysEnum.REFERRER]: point.label
                },
                value: point.count,
                percent: +(AgprMath.safeDivide(point.count, totalResults) * 100).toFixed(1)
            });
        }

        return of(data);
    }

    private mapReferralSources(response: GoogleAnalyticsReportResponseModel): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];

        Object.keys(response.sumOfCountsByBucketType).forEach((key) => {
            const value = response.sumOfCountsByBucketType[key];
            if (!value) {
                return;
            }
            const resKeyArray = key.split('_');
            const source = resKeyArray[0].toLocaleLowerCase();
            let metric = resKeyArray[1];
            if (metric === ExternalChartMetricsEnum.activeUsers) {
                metric = ExternalChartMetricsEnum.users;
            }
            if (resKeyArray.length >= 3) {
                switch (resKeyArray[2]) {
                    case ReferralSourceType.NEWVISITOR:
                        metric = metric === ExternalChartMetricsEnum.users ? ExternalChartMetricsEnum.usersNew : ExternalChartMetricsEnum.sessionsNew;
                        break;
                    case ReferralSourceType.RETURNVISITOR:
                        metric = metric === ExternalChartMetricsEnum.users ?
                            ExternalChartMetricsEnum.usersReturning : ExternalChartMetricsEnum.sessionsReturning;
                        break;
                    default:
                        // todo: add unknown slice if requested - NEP-30045
                        return;
                }
            }

            data.push({
                label: source,
                mappingKeys: {
                    [ReportsDataMappingKeysEnum.SOURCE]: source,
                    [ReportsDataMappingKeysEnum.GA_METRIC]: metric
                },
                value: response.sumOfCountsByBucketType[key]
            });
        });

        return of(data);
    }

    /**
     * Maps facet data to media type DataPointModel[]
     */
    private mapReferralTrend(response: GoogleAnalyticsReportResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const data: DataPointModel[] = [];
        const keys = Object.keys(response.countsPerDateByBucketType).filter(key => series.referralTypes.includes(key as GoogleAnalyticsReportTypesEnum));

        for (const key of keys) {
            response.countsPerDateByBucketType[key].forEach(point => {

                // api doesn't always return the data the same
                // sometimes it returns it on the hour (ex=> from=2:00 & to=3:00)
                // where other times it's now time (ex=> from: now & to=now+1day)
                // making the pointKey not match and for each time slice messing up the chart
                const fromDate = point.from - (point.from % this.oneDay);
                const toDate = point.to - (point.to % this.oneDay);
                const timeScale = getTimeScale(fromDate, toDate);
                const dataPoint = {
                    mappingKeys: {
                        [ReportsDataMappingKeysEnum.DATE]: fromDate,
                        [ReportsDataMappingKeysEnum.REFERRER_TYPE]: key
                    },
                    value: point.count,
                    valueLabel: this.localizationService.transform(`Reports.googleAnalyticsCharts.${key}`),
                    mentionCount: point.count,
                    startTimeExport: moment(fromDate).startOf(timeScale).format('YYYY/MM/DD hh:mm'),
                    endTimeExport: moment(toDate).startOf(timeScale).format('YYYY/MM/DD hh:mm'),
                    rawStartDate: fromDate,
                    rawEndDate: toDate,
                    timeScale: timeScale
                };

                data.push(dataPoint);
            });
        }

        return of(data);
    }

    /**
     * Maps facet data to media type DataPointModel[]
     */
    private mapCoverageImpactTrend(response: GoogleAnalyticsReportResponseModel, series: ReportSeriesModel): Observable<DataPointModel[]> {
        const data = [];
        const keys = Object.keys(response.countsPerDateByBucketType).filter(key => series.referralTypes.includes(key as GoogleAnalyticsReportTypesEnum));

        for (const key of keys) {
            response.countsPerDateByBucketType[key].forEach(point => {
                const fromDate = moment.tz(moment.tz(point.from, 'utc').format('YYYY-MM-DD HH:mm:ss'), this.userPreferenceTimezone).valueOf();
                const toDate = moment.tz(moment.tz(point.to, 'utc').format('YYYY-MM-DD HH:mm:ss'), this.userPreferenceTimezone).valueOf();
                const timeScale = getTimeScale(fromDate, toDate);
                const dataPoint = {
                    mappingKeys: {
                        [ReportsDataMappingKeysEnum.DATE]: moment(fromDate).startOf(timeScale).valueOf(),
                        [ReportsDataMappingKeysEnum.COVERAGE_IMPACT_TREND_METRIC]: key
                    },
                    value: point.count,
                    valueLabel: this.localizationService.transform(`Reports.googleAnalyticsCharts.${key}`),
                    mentionCount: point.count,
                    startTimeExport: moment(fromDate).startOf(timeScale).format('YYYY/MM/DD hh:mm'),
                    endTimeExport: moment(toDate).startOf(timeScale).format('YYYY/MM/DD hh:mm'),
                    rawStartDate: fromDate,
                    rawEndDate: toDate,
                    timeScale: timeScale
                };

                data.push(dataPoint);
            });
        }

        return of(data);
    }

}
